import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings, SITE_SETTINGS_KEY } from "@/hooks/use-site-settings";
import { siteSettingsSchema, DEFAULT_SITE_SETTINGS, type SiteSettings } from "@/lib/site-settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Save, Loader2 } from "lucide-react";

const contacts: { key: keyof SiteSettings; label: string; type: string }[] = [
  { key: "whatsapp_primary", label: "WhatsApp principal", type: "tel" },
  { key: "whatsapp_secondary", label: "WhatsApp secundário", type: "tel" },
  { key: "email", label: "E-mail institucional", type: "email" },
];
const socials: { key: keyof SiteSettings; label: string }[] = [
  { key: "instagram_url", label: "Instagram" }, { key: "linkedin_url", label: "LinkedIn" },
  { key: "youtube_url", label: "YouTube" }, { key: "facebook_url", label: "Facebook" }, { key: "twitter_url", label: "X / Twitter" },
];
export default function SiteSettingsManager() {
  const { settings, isLoading, error, refetch } = useSiteSettings();
  const [form, setForm] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const cache = useQueryClient();
  useEffect(() => { setForm(settings); }, [settings]);
  const dirty = JSON.stringify(form) !== JSON.stringify(settings);
  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);
  const change = (key: keyof SiteSettings, value: string) => setForm((current) => ({ ...current, [key]: value }));
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = siteSettingsSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message])));
      return;
    }
    setErrors({}); setSaving(true);
    const validated: SiteSettings = { ...DEFAULT_SITE_SETTINGS, ...parsed.data };
    const { error: saveError } = await supabase.from("site_settings").upsert({ id: true, ...validated }).select("id").single();
    setSaving(false);
    if (saveError) { toast({ title: "Não foi possível salvar", description: saveError.message, variant: "destructive" }); return; }
    cache.setQueryData(SITE_SETTINGS_KEY, validated);
    setForm(validated);
    toast({ title: "Configurações salvas" });
  };
  if (isLoading) return <p className="text-muted-foreground">Carregando configurações...</p>;
  if (error) return <div role="alert" className="space-y-3"><p>Não foi possível carregar as configurações.</p><Button variant="outline" onClick={() => refetch()}>Tentar novamente</Button></div>;
  return <form onSubmit={save} className="max-w-3xl space-y-8">
    <section className="space-y-4">
      <h2 className="font-heading font-semibold border-b pb-3">Contatos</h2>
      <div className="grid sm:grid-cols-2 gap-4">{contacts.map(({ key, label, type }) => <div key={key} className="space-y-2">
        <Label htmlFor={key}>{label}</Label><Input id={key} type={type} value={form[key]} onChange={(e) => change(key, e.target.value)} aria-invalid={!!errors[key]} />
        {errors[key] && <p className="text-sm text-destructive">{errors[key]}</p>}
      </div>)}</div>
      <div className="space-y-2"><Label htmlFor="whatsapp_message">Mensagem do WhatsApp flutuante</Label>
        <Textarea id="whatsapp_message" value={form.whatsapp_message} maxLength={500} onChange={(e) => change("whatsapp_message", e.target.value)} />
        {errors.whatsapp_message && <p className="text-sm text-destructive">{errors.whatsapp_message}</p>}
      </div>
    </section>
    <section className="space-y-4"><h2 className="font-heading font-semibold border-b pb-3">Redes sociais</h2>
      <div className="grid sm:grid-cols-2 gap-4">{socials.map(({ key, label }) => <div key={key} className="space-y-2">
        <Label htmlFor={key}>{label}</Label><Input id={key} type="url" placeholder="https://" value={form[key]} onChange={(e) => change(key, e.target.value)} aria-invalid={!!errors[key]} />
        {errors[key] && <p className="text-sm text-destructive">{errors[key]}</p>}
      </div>)}</div>
    </section>
    <div className="flex gap-3 border-t pt-4"><Button type="submit" disabled={saving || !dirty}>{saving ? <Loader2 className="animate-spin" /> : <Save />}Salvar alterações</Button>
      <Button type="button" variant="ghost" disabled={saving || !dirty} onClick={() => { setForm(settings); setErrors({}); }}>Descartar alterações</Button></div>
  </form>;
}